import { useEffect, useCallback } from 'react';

const PerformanceOptimizer = () => {
  // Preload critical resources
  const preloadCriticalResources = useCallback(() => {
    const criticalImages = [
      '/images/Uniway_Logo.png',
      '/images/Advisory.png',
      '/images/Microservice.png',
      '/images/Appdevelop2.png',
      '/images/slide-1.png',
      '/images/slide-4.png',
    ];

    criticalImages.forEach(src => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.fetchPriority = 'high';
      document.head.appendChild(link);
    });

    const fontLink = document.createElement('link');
    fontLink.rel = 'preload';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
    fontLink.as = 'style';
    document.head.appendChild(fontLink);
  }, []);

  // Optimize font loading
  const optimizeFonts = useCallback(() => {
    if ('fonts' in document) {
      document.fonts.ready.then(() => {
        document.documentElement.classList.add('fonts-loaded');
      });
    }
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then(registration => {
          console.log('SW registered:', registration);
        })
        .catch(error => {
          console.log('SW registration failed:', error);
        });
    }
  }, []);

  // Monitor performance
  const monitorPerformance = useCallback(() => {
    if ('performance' in window) {
      const observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            console.log('LCP:', entry.startTime);
          }
          if (entry.entryType === 'first-input') {
            console.log('FID:', entry.processingStart - entry.startTime);
          }
          if (entry.entryType === 'layout-shift') {
            console.log('CLS:', entry.value);
          }
        }
      });

      observer.observe({
        entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'],
      });

      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = performance.getEntriesByType('navigation')[0];
          if (perfData) {
            console.log('Page Load Time:', perfData.loadEventEnd - perfData.loadEventStart);
            console.log('DOM Content Loaded:', perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart);
            console.log('Time to Interactive:', perfData.domInteractive - perfData.navigationStart);
          }
        }, 0);
      });
    }
  }, []);

  // Optimize images
  const optimizeImages = useCallback(() => {
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      if (!img.loading) img.loading = 'lazy';
      if (!img.decoding) img.decoding = 'async';
    });
  }, []);

  // Lazy loading setup
  const setupLazyLoading = useCallback(() => {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              obs.unobserve(img);
            }
          }
        });
      });

      const lazyImages = document.querySelectorAll('img[data-src]');
      lazyImages.forEach(img => observer.observe(img));
    }
  }, []);

  // Scroll optimization
  const optimizeScroll = useCallback(() => {
    let ticking = false;

    const updateScrollPosition = () => {
      // Add scroll-based optimizations here
      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollPosition);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });

    return () => {
      window.removeEventListener('scroll', requestTick);
    };
  }, []);

  useEffect(() => {
    preloadCriticalResources();
    optimizeFonts();

    if (process.env.NODE_ENV === 'production') {
      registerServiceWorker();
    }

    monitorPerformance();
    optimizeImages();
    setupLazyLoading();

    const cleanupScroll = optimizeScroll();

    return () => {
      cleanupScroll();
    };
  }, [
    preloadCriticalResources,
    optimizeFonts,
    registerServiceWorker,
    monitorPerformance,
    optimizeImages,
    setupLazyLoading,
    optimizeScroll,
  ]);

  return null;
};

export default PerformanceOptimizer;
